import React, { useState } from 'react';
import { X, Send, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

let _rowSeq = 0;
const emptyRow = () => ({ id: `r${++_rowSeq}`, name: '', email: '' });
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Send an assessment/interview to MULTIPLE candidates at once.
 * Props:
 *   open, onClose
 *   itemCount        number of selected items (assessments/interviews)
 *   onSendAll(recipients[]) -> Promise<{ sent, failed }>
 *   title, accent
 */
export default function BulkSendDialog({ open, onClose, itemCount = 1, onSendAll, title = 'Bulk send', accent = '#0076A3' }) {
  const [rows, setRows] = useState([emptyRow()]);
  const [paste, setPaste] = useState('');
  const [sending, setSending] = useState(false);

  if (!open) return null;

  const reset = () => { setRows([emptyRow()]); setPaste(''); };
  const close = () => { if (!sending) { reset(); onClose(); } };

  const setRow = (i, key, val) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [key]: val } : row)));
  const addRow = () => setRows((r) => [...r, emptyRow()]);
  const removeRow = (i) => setRows((r) => (r.length === 1 ? r : r.filter((_, idx) => idx !== i)));

  const applyPaste = () => {
    const found = (paste.match(/[^\s,;<>()]+@[^\s,;<>()]+\.[^\s,;<>()]+/g) || []);
    if (found.length === 0) { toast.error('No valid emails found in the pasted text'); return; }
    const existing = new Set(rows.map((r) => r.email.trim().toLowerCase()).filter(Boolean));
    const newRows = found
      .map((e) => e.trim().toLowerCase())
      .filter((e) => !existing.has(e))
      .map((e) => ({ id: `r${++_rowSeq}`, name: '', email: e }));
    setRows((r) => [...r.filter((x) => x.email.trim()), ...newRows].length ? [...r.filter((x) => x.email.trim()), ...newRows] : [emptyRow()]);
    setPaste('');
    toast.success(`Added ${newRows.length} recipient${newRows.length === 1 ? '' : 's'}`);
  };

  const submit = async () => {
    const recipients = rows
      .map((r) => ({ candidate_name: r.name.trim(), candidate_email: r.email.trim() }))
      .filter((r) => r.candidate_email);
    if (recipients.length === 0) { toast.error('Add at least one candidate email'); return; }
    const invalid = recipients.find((r) => !EMAIL_RE.test(r.candidate_email));
    if (invalid) { toast.error(`Invalid email: ${invalid.candidate_email}`); return; }

    setSending(true);
    try {
      const { sent, failed } = await onSendAll(recipients);
      if (failed > 0) {
        toast.warning(`Sent ${sent} invite${sent === 1 ? '' : 's'}, ${failed} failed.`);
      } else {
        toast.success(`Sent ${sent} invite${sent === 1 ? '' : 's'} successfully.`);
      }
      reset();
      onClose();
    } catch (e) {
      toast.error('Bulk send failed. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const validCount = rows.filter((r) => r.email.trim()).length;
  const totalInvites = validCount * Math.max(1, itemCount);

  return (
    <div data-testid="bulk-send-dialog" onClick={close}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={title} style={{ width: '100%', maxWidth: 520, background: '#fff', borderRadius: 16, padding: '1.5rem', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', maxHeight: '88vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <h3 style={{ fontSize: '1.0625rem', fontWeight: 700, color: '#002A38' }}>{title}</h3>
          <button data-testid="bulk-send-close" onClick={close} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#94A3B8' }}><X size={20} /></button>
        </div>
        <p style={{ fontSize: '0.8125rem', color: '#64748B', marginBottom: '1rem' }}>
          {itemCount > 1 ? `${itemCount} items selected — ` : ''}each candidate below receives an invite.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.75rem' }}>
          {rows.map((row, i) => (
            <div key={row.id} data-testid="bulk-recipient-row" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                data-testid={`bulk-name-${i}`}
                value={row.name}
                onChange={(e) => setRow(i, 'name', e.target.value)}
                placeholder="Name (optional)"
                style={{ flex: '1 1 40%', padding: '0.5rem 0.625rem', borderRadius: 8, border: '1px solid #ECE7DE', fontSize: '0.8125rem', outline: 'none' }}
              />
              <input
                data-testid={`bulk-email-${i}`}
                type="email"
                value={row.email}
                onChange={(e) => setRow(i, 'email', e.target.value)}
                placeholder="email@example.com"
                style={{ flex: '1 1 55%', padding: '0.5rem 0.625rem', borderRadius: 8, border: '1px solid #ECE7DE', fontSize: '0.8125rem', outline: 'none' }}
              />
              <button data-testid={`bulk-remove-${i}`} onClick={() => removeRow(i)} title="Remove"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#CBD5E1', padding: 4 }}>
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>

        <button data-testid="bulk-add-row" onClick={addRow}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', background: 'transparent', border: 'none', color: accent, fontWeight: 600, fontSize: '0.8125rem', cursor: 'pointer', marginBottom: '1rem' }}>
          <Plus size={15} /> Add candidate
        </button>

        <div style={{ borderTop: '1px dashed #ECE7DE', paddingTop: '0.875rem', marginBottom: '1rem' }}>
          <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748B' }}>Or paste a list of emails</label>
          <textarea
            data-testid="bulk-paste"
            value={paste}
            onChange={(e) => setPaste(e.target.value)}
            placeholder="jane@acme.com, john@acme.com …"
            style={{ width: '100%', marginTop: '0.375rem', minHeight: 56, padding: '0.5rem 0.625rem', borderRadius: 8, border: '1px solid #ECE7DE', fontSize: '0.8125rem', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
          />
          <button data-testid="bulk-paste-apply" onClick={applyPaste} disabled={!paste.trim()}
            style={{ marginTop: '0.5rem', padding: '0.375rem 0.875rem', borderRadius: 999, background: '#F1F5F9', color: '#475569', border: 'none', fontWeight: 600, fontSize: '0.75rem', cursor: paste.trim() ? 'pointer' : 'default' }}>
            Add pasted emails
          </button>
        </div>

        <div style={{ display: 'flex', gap: '0.625rem', justifyContent: 'space-between', alignItems: 'center' }}>
          <span data-testid="bulk-invite-count" style={{ fontSize: '0.75rem', color: '#64748B' }}>
            {totalInvites} invite{totalInvites === 1 ? '' : 's'} will be sent
          </span>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={close} disabled={sending} style={{ padding: '0.5rem 1rem', borderRadius: 999, background: '#F1F5F9', color: '#475569', border: 'none', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
            <button data-testid="bulk-send-submit" onClick={submit} disabled={sending || validCount === 0}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 1.1rem', borderRadius: 999, background: accent, color: '#fff', border: 'none', fontWeight: 600, cursor: sending || validCount === 0 ? 'default' : 'pointer', opacity: sending || validCount === 0 ? 0.6 : 1 }}>
              <Send size={15} /> {sending ? 'Sending…' : 'Send all'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
